'use client'

import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { Calendar, MapPin } from 'lucide-react'
import { resolveImageUrl } from '~/lib/images'
import { summarizeLocation } from '~/lib/location'
import { formatEventDate } from '~/lib/event-dates'

export interface SwipeEvent {
  id: string
  title: string
  description?: string
  image_url?: string | null
  location?: string | null
  online?: boolean
  start_datetime?: string
  end_datetime?: string
  timezone?: string | null
  categories?: { name: string }[]
  subcategories?: { name: string }[]
  hosts?: { user_id: string; full_name?: string }[]
}

// Horizontal drag distance (px) that commits a swipe.
const SWIPE_THRESHOLD = 110

/**
 * A draggable event card. Hand-rolled pointer-event dragging (no gesture lib
 * in this repo): drag right = like, drag left = skip; the parent deck owns
 * the action semantics via `onSwipe`.
 */
// Source subsection → short label shown on the card.
const SOURCE_LABEL: Record<string, string> = {
  personalised: 'For you',
  interest: 'Your interests',
  community: 'Facilitator you follow',
  reviews: 'Based on your reviews',
  outside_comfort_zone: 'Something new',
}

export default function SwipeCard({
  event,
  score,
  why,
  source,
  active,
  onSwipe,
  forcedDirection,
}: {
  event: SwipeEvent
  score?: number
  /** Unified scorer: the dominant-factor reason (shown instead of a %). */
  why?: string
  /** Unified scorer: which match subsection this card came from. */
  source?: string
  active: boolean
  onSwipe: (direction: 'left' | 'right') => void
  /** Set by the deck's action buttons to play the same exit animation. */
  forcedDirection?: 'left' | 'right' | null
}) {
  const [drag, setDrag] = useState({ x: 0, y: 0, dragging: false })
  const [leaving, setLeaving] = useState<null | 'left' | 'right'>(null)
  const start = useRef<{ x: number; y: number } | null>(null)

  const handlePointerDown = (e: React.PointerEvent) => {
    if (!active || leaving) return
    start.current = { x: e.clientX, y: e.clientY }
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    setDrag({ x: 0, y: 0, dragging: true })
  }

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!start.current || leaving) return
    setDrag({
      x: e.clientX - start.current.x,
      y: (e.clientY - start.current.y) * 0.4,
      dragging: true,
    })
  }

  const settle = () => {
    start.current = null
    setDrag({ x: 0, y: 0, dragging: false })
  }

  const fling = (direction: 'left' | 'right') => {
    setLeaving(direction)
    // Let the exit transition play before the deck advances.
    setTimeout(() => onSwipe(direction), 220)
  }

  const handlePointerUp = () => {
    if (!start.current || leaving) return
    const dx = drag.x
    start.current = null
    if (dx > SWIPE_THRESHOLD) fling('right')
    else if (dx < -SWIPE_THRESHOLD) fling('left')
    else settle()
  }

  useEffect(() => {
    if (forcedDirection && active && !leaving) fling(forcedDirection)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forcedDirection])

  const x = leaving ? (leaving === 'right' ? 600 : -600) : drag.x
  const rotation = x / 18
  const likeOpacity = Math.max(0, Math.min(drag.x / SWIPE_THRESHOLD, 1))
  const skipOpacity = Math.max(0, Math.min(-drag.x / SWIPE_THRESHOLD, 1))
  const tags = [
    ...(event.subcategories ?? []).map((s) => s.name),
    ...(event.categories ?? []).map((c) => c.name),
  ].slice(0, 3)

  return (
    <div
      className="absolute inset-0 touch-none select-none"
      style={{
        transform: `translate(${x}px, ${drag.y}px) rotate(${rotation}deg)`,
        transition: drag.dragging ? 'none' : 'transform 0.25s ease',
        opacity: leaving ? 0 : 1,
        cursor: active ? (drag.dragging ? 'grabbing' : 'grab') : 'default',
        pointerEvents: active ? 'auto' : 'none',
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={settle}
    >
      <div className="flex h-full flex-col overflow-hidden rounded-[22px] border border-[#EFE7D7] bg-[#FFFDF8] shadow-[0_18px_40px_rgba(44,45,43,0.14)]">
        <div className="relative h-1/2 w-full flex-none overflow-hidden">
          <Image
            src={resolveImageUrl(
              event.image_url?.includes(',')
                ? event.image_url.split(',')[0]?.trim()
                : event.image_url,
            )}
            alt={event.title}
            fill
            className="object-cover"
            draggable={false}
          />
          {/* Unified scorer shows a source label (no false-precision %); the
              raw score falls back only for legacy/pre-unified decks. */}
          {source ? (
            <span className="absolute right-3 top-3 rounded-full bg-[#FFFDF8]/90 px-2.5 py-1 text-xs font-bold text-[#4E7A6F] backdrop-blur">
              {SOURCE_LABEL[source] ?? 'For you'}
            </span>
          ) : (
            typeof score === 'number' &&
            score > 0 && (
              <span className="absolute right-3 top-3 rounded-full bg-[#FFFDF8]/90 px-2.5 py-1 text-xs font-bold text-[#4E7A6F] backdrop-blur">
                {score}% match
              </span>
            )
          )}
          {/* Drag feedback stamps */}
          <span
            className="absolute left-4 top-4 rotate-[-12deg] rounded-lg border-4 border-emerald-500 px-3 py-1 text-xl font-extrabold text-emerald-500"
            style={{ opacity: likeOpacity }}
          >
            LIKE
          </span>
          <span
            className="absolute right-4 top-4 rotate-[12deg] rounded-lg border-4 border-[#8A8475] px-3 py-1 text-xl font-extrabold text-[#8A8475]"
            style={{ opacity: skipOpacity }}
          >
            SKIP
          </span>
        </div>
        <div className="flex flex-1 flex-col gap-2 p-5">
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {tags.map((t) => (
                <span
                  key={t}
                  className="rounded-full bg-[#75A096]/10 px-2.5 py-0.5 text-[11px] font-semibold text-[#4E7A6F]"
                >
                  {t}
                </span>
              ))}
            </div>
          )}
          <h3 className="text-lg font-bold leading-snug text-[#2C2D2B] line-clamp-2">
            {event.title}
          </h3>
          {why && (
            <p className="text-xs font-semibold text-[#DE866D]">{why}</p>
          )}
          <div className="flex items-center gap-1.5 text-sm text-[#5E594F]">
            <Calendar className="h-4 w-4 flex-none text-[#8A8475]" />
            {formatEventDate(event as any)}
          </div>
          <div className="flex items-center gap-1.5 text-sm text-[#5E594F]">
            <MapPin className="h-4 w-4 flex-none text-[#8A8475]" />
            {event.online ? 'Online' : summarizeLocation(event.location) || 'Location TBA'}
          </div>
          {event.description && (
            <p className="mt-1 text-sm text-[#5E594F] line-clamp-3">
              {event.description}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
